import classNames from "classnames";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { Fragment, SetStateAction, Dispatch, PropsWithChildren } from "react";

interface IProps extends PropsWithChildren {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>> | ((args: boolean) => void);
  title?: string;
  description?: string;
  styles?: {
    container?: string;
    panel?: string;
    childrenContainer?: string;
    closeButton?: string;
    closeButtonIcon?: string;
    overflowY?: boolean;
    panelHeight?: string;
    panelOverflowY?: boolean;
    childContainerOverflowY?: boolean;
    childContainerHeight?: string;
    childrenPadding?: boolean;
    topBar?: string;
  };
  headerHide?: boolean;
  onCloseProp?: () => void;
  closeOutside?: boolean | ((args: boolean) => void);
}

export default function DialogOne({
  open,
  title,
  setOpen,
  children,
  description,
  headerHide = false,
  styles = {},
  closeOutside = true,
  onCloseProp
}: IProps) {
  function onClose() {
    if (onCloseProp) {
      onCloseProp();
    }
    setOpen(false);
  }

  return (
    <Transition.Root
      show={open}
      as={Fragment}
    >
      <Dialog
        as="div"
        className={classNames(styles?.container, "relative z-40")}
        onClose={() => closeOutside && onClose()}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className={classNames("fixed inset-0 z-10 overflow-y-auto")}>
          <div
            className={`flex min-h-full items-center justify-center text-center sm:p-0`}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={classNames(
                  "relative transform rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 mx-0 md:m-4 mb-0 rounded-b-none lg:rounded-b-lg w-3/4",
                  styles?.panel || " sm:max-w-sm md:max-w-2xl",
                  styles?.panelHeight || "",
                  styles?.panelOverflowY && "overflow-y-auto overflow-visible"
                )}
              >
                {!headerHide && (
                  <div
                    className={classNames(
                      "p-4 xs:p-2 border-b",
                      styles?.topBar
                    )}
                  >
                    <div>
                      <button
                        className={classNames(
                          "float-right",
                          styles?.closeButton
                        )}
                        onClick={() => onClose()}
                      >
                        <XMarkIcon
                          className={classNames(
                            "text-gray-700",
                            styles?.closeButtonIcon || "w-5 h-5"
                          )}
                        />
                      </button>
                    </div>

                    {title && (
                      <Dialog.Title>
                        <p className="text-base font-medium mb-1">{title}</p>
                      </Dialog.Title>
                    )}

                    {description && (
                      <Dialog.Description>
                        <p className="text-xs text-gray-500">{description}</p>
                      </Dialog.Description>
                    )}
                  </div>
                )}

                <div
                  className={classNames(
                    styles?.childrenPadding && "p-4",
                    styles?.childrenContainer ?? "",
                    styles?.childContainerHeight || "",
                    styles?.childContainerOverflowY && "overflow-y-auto flex-1"
                  )}
                >
                  {children}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
